import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../utils/api';

import {
  Table,
  Card,
  Button,
  Tag,
  Space,
  Input,
  Select,
  DatePicker,
  Modal,
  Form,
  message,
  Statistic,
  Row,
  Col,
  Descriptions,
  Badge,
  Tooltip,
  Popconfirm,
  Checkbox,
  Typography,
  List,
  Flex,
  Drawer,
  Spin,
  Empty
} from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  BankOutlined,
  DollarOutlined,
  CreditCardOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;
const { Search } = Input;

const Transactions = () => {
  // Mobile responsiveness detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('cards');

  // Responsive handler
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      setViewMode(mobile ? 'cards' : 'table');
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial call

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    userId: '',
    dateRange: null,
    search: ''
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: isMobile ? 8 : 20,
    total: 0
  });
  const [stats, setStats] = useState({
    totalTransactions: 0,
    totalAmount: 0,
    pendingTransactions: 0,
    completedTransactions: 0
  });
  const [detailsModal, setDetailsModal] = useState({
    visible: false,
    transaction: null
  });
  const [actionModal, setActionModal] = useState({
    visible: false,
    type: '',
    transaction: null
  });
  const [bulkActionModal, setBulkActionModal] = useState({
    visible: false,
    action: ''
  });

  useEffect(() => {
    fetchTransactions();
  }, [pagination.current, pagination.pageSize, filters]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters
      };

      if (filters.dateRange) {
        params.startDate = filters.dateRange[0].format('YYYY-MM-DD');
        params.endDate = filters.dateRange[1].format('YYYY-MM-DD');
      }

      const response = await api.getTransactions(params);

      setTransactions(response.data.transactions);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total
      }));
      setStats(response.data.stats);
    } catch (error) {
      message.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (transactionId, action, reason = '') => {
    try {
      setLoading(true);
      
      // Map action to status based on the backend's expected values
      let status;
      if (action === 'approve') {
        status = 'approved';
      } else if (action === 'reject') {
        status = 'rejected';
      } else if (action === 'complete') {
        status = 'completed';
      } else {
        throw new Error('Invalid action');
      }

      const response = await api.updateTransactionStatus(transactionId, { 
        status,
        reason
      });
      
      if (response.data && response.data.success) {
        // Update the transaction in the local state to avoid a full refresh
        setTransactions(prevTransactions => 
          prevTransactions.map(tx => 
            tx._id === transactionId 
              ? { 
                  ...tx, 
                  status: status === 'approved' ? 'completed' : status, // Map 'approved' to 'completed' for frontend
                  updatedAt: new Date().toISOString() 
                }
              : tx
          )
        );
        
        // Also update the stats
        setStats(prevStats => {
          const newStats = { ...prevStats };
          
          if (status === 'completed' || status === 'approved') {
            newStats.completedTransactions = (prevStats.completedTransactions || 0) + 1;
            newStats.pendingTransactions = Math.max(0, (prevStats.pendingTransactions || 0) - 1);
          } else if (status === 'rejected') {
            newStats.pendingTransactions = Math.max(0, (prevStats.pendingTransactions || 0) - 1);
          }
          
          return newStats;
        });
        
        message.success(`Transaction ${status} successfully`);
      } else {
        throw new Error(response?.data?.message || 'Failed to update transaction');
      }
    } catch (error) {
      console.error('Error updating transaction status:', error);
      message.error(error.response?.data?.message || error.message || 'Failed to update transaction status. Please try again.');
    } finally {
      setLoading(false);
      setActionModal({ visible: false, type: '', transaction: null });
      // Refresh the transactions list to ensure consistency
      fetchTransactions();
    }
  };

  const handleBulkAction = async (action, reason) => {
    try {
      const response = await api.bulkTransactionAction({
        transactionIds: selectedRowKeys,
        action,
        reason
      });

      message.success(
        `Bulk action completed: ${response.data.successful} successful, ${response.data.failed} failed`
      );
      fetchTransactions();
      setSelectedRowKeys([]);
      setBulkActionModal({ visible: false, action: '' });
    } catch (error) {
      message.error('Failed to perform bulk action');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      processing: 'blue',
      completed: 'green',
      failed: 'red',
      cancelled: 'red'
    };
    return colors[status] || 'default';
  };

  const getTypeColor = (type) => {
    const colors = {
      deposit: 'green',
      withdrawal: 'red',
      credit: 'cyan',
      debit: 'magenta'
    };
    return colors[type] || 'default';
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const columns = [
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      width: 120,
      render: (text) => (
        <Text code style={{ fontSize: '11px' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'User',
      dataIndex: ['userId', 'name'],
      key: 'user',
      width: 150,
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500, fontSize: '12px' }}>
            {record.userId?.name || 'N/A'}
          </div>
          <div style={{ fontSize: '10px', color: '#666' }}>
            {record.userId?.email || 'N/A'}
          </div>
        </div>
      )
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type) => (
        <Tag color={getTypeColor(type)} style={{ fontSize: '10px' }}>
          {type.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right',
      render: (amount, record) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: '12px' }}>
            {formatAmount(amount)}
          </div>
          {record.fees?.totalFees > 0 && (
            <div style={{ fontSize: '10px', color: '#666' }}>
              Fee: {formatAmount(record.fees.totalFees)}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Badge
          status={getStatusColor(status) === 'green' ? 'success' : 
                 getStatusColor(status) === 'orange' ? 'warning' :
                 getStatusColor(status) === 'blue' ? 'processing' : 'error'}
          text={status.charAt(0).toUpperCase() + status.slice(1)}
        />
      )
    },
    {
      title: 'Method',
      dataIndex: 'method',
      key: 'method',
      width: 100,
      render: (method) => (
        <Tag style={{ fontSize: '10px' }}>
          {method.replace('_', ' ').toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Date',
      dataIndex: 'initiatedAt',
      key: 'date',
      width: 120,
      render: (date) => (
        <div>
          <div style={{ fontSize: '11px' }}>
            {new Date(date).toLocaleDateString()}
          </div>
          <div style={{ fontSize: '10px', color: '#666' }}>
            {new Date(date).toLocaleTimeString()}
          </div>
        </div>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              style={{ 
                borderRadius: '6px',
                background: '#f0f8ff',
                color: '#1890ff',
                border: '1px solid #d6e4ff'
              }}
              onClick={() => setDetailsModal({ visible: true, transaction: record })}
            />
          </Tooltip>
          {(record.type === 'withdrawal' || record.type === 'deposit') && record.status === 'pending' && (
            <>
              <Tooltip title="Approve Transaction">
                <Button
                  type="text"
                  size="small"
                  icon={<CheckOutlined />}
                  style={{ 
                    borderRadius: '6px',
                    background: '#f6ffed',
                    color: '#52c41a',
                    border: '1px solid #b7eb8f'
                  }}
                  onClick={() => setActionModal({ 
                    visible: true, 
                    type: 'approve', 
                    transaction: record 
                  })}
                />
              </Tooltip>
              <Tooltip title="Reject Transaction">
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  style={{ 
                    borderRadius: '6px',
                    background: '#fff2f0',
                    color: '#ff4d4f',
                    border: '1px solid #ffccc7'
                  }}
                  onClick={() => setActionModal({ 
                    visible: true, 
                    type: 'reject', 
                    transaction: record 
                  })}
                />
              </Tooltip>
            </>
          )}
          {(record.type === 'withdrawal' || record.type === 'deposit') && record.status === 'processing' && (
            <Tooltip title="Mark as Completed">
              <Button
                type="text"
                size="small"
                icon={<CheckOutlined />}
                style={{ 
                  borderRadius: '6px',
                  background: '#e6f7ff',
                  color: '#1890ff',
                  border: '1px solid #91d5ff'
                }}
                onClick={() => setActionModal({ 
                  visible: true, 
                  type: 'complete', 
                  transaction: record 
                })}
              />
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (record) => ({
      disabled: !['withdrawal', 'deposit'].includes(record.type) || record.status !== 'pending'
    })
  };

  // Add custom styles
  const customStyles = `
    .table-row-light:hover {
      background-color: #f8f9fa !important;
    }
    .table-row-dark:hover {
      background-color: #f0f2f5 !important;
    }
    .ant-table-tbody > tr > td {
      border-bottom: 1px solid #f0f0f0;
      padding: 12px 16px;
    }
    .ant-table-thead > tr > th {
      background: linear-gradient(90deg, #fafbfc 0%, #f8f9fa 100%);
      border-bottom: 2px solid #e8e8e8;
      font-weight: 600;
      color: #333;
    }
  `;

  if (loading && transactions.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="fade-in" style={{ padding: '0 4px' }}>
      {/* Modern Header */}
      <div className="transactions-header" style={{
        background: 'linear-gradient(135deg, #13c2c2 0%, #36cfc9 100%)',
        borderRadius: isMobile ? '20px' : '24px',
        padding: isMobile ? '24px 20px' : '40px 32px',
        marginBottom: '24px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(19, 194, 194, 0.3)'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '200px',
          height: '200px',
          background: 'rgba(255,255,255,0.08)',
          borderRadius: '50%',
          transform: 'translate(50%, -50%)'
        }} />
        
        <Flex justify="space-between" align="center" wrap="wrap" gap={16}>
          <div style={{ zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
              <div style={{
                background: 'rgba(255,255,255,0.2)',
                borderRadius: '16px',
                padding: '12px',
                backdropFilter: 'blur(10px)'
              }}>
                <BankOutlined style={{ fontSize: isMobile ? '24px' : '28px' }} />
              </div>
              <Title level={isMobile ? 3 : 1} style={{ 
                margin: 0, 
                color: 'white', 
                fontSize: isMobile ? '22px' : '32px', 
                fontWeight: 800,
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                letterSpacing: '-0.5px'
              }}>
                Transactions Management
              </Title>
            </div>
            <Text style={{ 
              color: 'rgba(255,255,255,0.9)', 
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: 500
            }}>
              Monitor and manage all trading transactions
            </Text>
          </div>
          <Space size={isMobile ? 8 : 12}>
            {!isMobile && (
              <>
                <Button
                  icon={<DownloadOutlined />}
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    border: 'none',
                    color: 'white',
                    borderRadius: '12px',
                    height: '44px',
                    fontWeight: '600'
                  }}
                >
                  Export
                </Button>
              </>
            )}
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchTransactions}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: 'white',
                borderRadius: '12px',
                height: isMobile ? '40px' : '44px',
                fontWeight: '600'
              }}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        </Flex>
      </div>

      {/* Enhanced Statistics Cards */}
      <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]} style={{ marginBottom: isMobile ? 16 : 24 }}>
        <Col xs={12} sm={12} md={6} lg={6}>
          <Card 
            className="slide-up stats-card-modern" 
            style={{
              background: 'linear-gradient(135deg, #13c2c2 0%, #36cfc9 100%)',
              border: 'none',
              borderRadius: isMobile ? '16px' : '20px',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              minHeight: isMobile ? '100px' : '120px',
              height: isMobile ? '100px' : '120px'
            }}
            bodyStyle={{ 
              padding: isMobile ? '16px' : '20px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <div style={{
              position: 'absolute',
              top: isMobile ? '-5px' : '-10px',
              right: isMobile ? '-5px' : '-10px',
              fontSize: isMobile ? '40px' : '60px',
              opacity: 0.2,
              color: 'white'
            }}>
              💳
            </div>
            <Statistic
              title={
                <span style={{ 
                  color: 'rgba(255,255,255,0.9)', 
                  fontWeight: 600,
                  fontSize: isMobile ? '11px' : '14px'
                }}>
                  Total Transactions
                </span>
              }
              value={stats.totalTransactions || 0}
              precision={0}
              valueStyle={{ 
                color: 'white', 
                fontSize: isMobile ? '18px' : '24px', 
                fontWeight: 'bold',
                lineHeight: 1.2
              }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6} lg={6}>
          <Card 
            className="slide-up stats-card-modern" 
            style={{
              background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
              border: 'none',
              borderRadius: isMobile ? '16px' : '20px',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              minHeight: isMobile ? '100px' : '120px',
              height: isMobile ? '100px' : '120px'
            }}
            bodyStyle={{ 
              padding: isMobile ? '16px' : '20px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <div style={{
              position: 'absolute',
              top: isMobile ? '-5px' : '-10px',
              right: isMobile ? '-5px' : '-10px',
              fontSize: isMobile ? '40px' : '60px',
              opacity: 0.2,
              color: 'white'
            }}>
              ✅
            </div>
            <Statistic
              title={
                <span style={{ 
                  color: 'rgba(255,255,255,0.9)', 
                  fontWeight: 600,
                  fontSize: isMobile ? '11px' : '14px'
                }}>
                  Completed
                </span>
              }
              value={stats.completedTransactions || 0}
              precision={0}
              valueStyle={{ 
                color: 'white', 
                fontSize: isMobile ? '18px' : '24px', 
                fontWeight: 'bold',
                lineHeight: 1.2
              }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6} lg={6}>
          <Card 
            className="slide-up stats-card-modern" 
            style={{
              background: 'linear-gradient(135deg, #faad14 0%, #ffc53d 100%)',
              border: 'none',
              borderRadius: isMobile ? '16px' : '20px',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              minHeight: isMobile ? '100px' : '120px',
              height: isMobile ? '100px' : '120px'
            }}
            bodyStyle={{ 
              padding: isMobile ? '16px' : '20px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <div style={{
              position: 'absolute',
              top: isMobile ? '-5px' : '-10px',
              right: isMobile ? '-5px' : '-10px',
              fontSize: isMobile ? '40px' : '60px',
              opacity: 0.2,
              color: 'white'
            }}>
              ⏳
            </div>
            <Statistic
              title={
                <span style={{ 
                  color: 'rgba(255,255,255,0.9)', 
                  fontWeight: 600,
                  fontSize: isMobile ? '11px' : '14px'
                }}>
                  Pending
                </span>
              }
              value={stats.pendingTransactions || 0}
              precision={0}
              valueStyle={{ 
                color: 'white', 
                fontSize: isMobile ? '18px' : '24px', 
                fontWeight: 'bold',
                lineHeight: 1.2
              }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6} lg={6}>
          <Card 
            className="slide-up stats-card-modern" 
            style={{
              background: 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)',
              border: 'none',
              borderRadius: isMobile ? '16px' : '20px',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              minHeight: isMobile ? '100px' : '120px',
              height: isMobile ? '100px' : '120px'
            }}
            bodyStyle={{ 
              padding: isMobile ? '16px' : '20px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <div style={{
              position: 'absolute',
              top: isMobile ? '-5px' : '-10px',
              right: isMobile ? '-5px' : '-10px',
              fontSize: isMobile ? '40px' : '60px',
              opacity: 0.2,
              color: 'white'
            }}>
              💰
            </div>
            <Statistic
              title={
                <span style={{ 
                  color: 'rgba(255,255,255,0.9)', 
                  fontWeight: 600,
                  fontSize: isMobile ? '11px' : '14px'
                }}>
                  Total Amount
                </span>
              }
              value={Math.abs(stats.totalAmount || 0)}
              precision={2}
              prefix="₹"
              valueStyle={{ 
                color: 'white', 
                fontSize: isMobile ? '16px' : '22px', 
                fontWeight: 'bold',
                lineHeight: 1.2
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Mobile-Friendly Filters */}
      {isMobile ? (
        <>
          <Card 
            style={{ 
              marginBottom: 20, 
              borderRadius: '20px',
              border: 'none',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)'
            }} 
            className="slide-up"
            bodyStyle={{ padding: '20px' }}
          >
            <div style={{ marginBottom: 16 }}>
              <Search
                placeholder="🔍 Search transactions by user, type, or amount..."
                allowClear
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                style={{ 
                  borderRadius: '16px',
                  border: '2px solid #f1f5f9',
                  background: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  fontSize: '16px'
                }}
                size="large"
              />
            </div>
            <Button
              block
              icon={<FilterOutlined />}
              onClick={() => setShowFilters(!showFilters)}
              style={{ 
                borderRadius: '16px',
                height: '48px',
                fontSize: '16px',
                fontWeight: '600',
                background: showFilters 
                  ? 'linear-gradient(135deg, #13c2c2 0%, #36cfc9 100%)' 
                  : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                borderColor: showFilters ? '#13c2c2' : '#e2e8f0',
                color: showFilters ? 'white' : '#475569',
                border: 'none',
                boxShadow: showFilters 
                  ? '0 4px 12px rgba(19, 194, 194, 0.3)' 
                  : '0 2px 8px rgba(0,0,0,0.06)',
                transition: 'all 0.3s ease'
              }}
            >
              {showFilters ? '🔽 Hide Filters' : '🔼 Show Filters'}
            </Button>
          </Card>

          {/* Mobile Filters Drawer */}
          <Drawer
            title="🔍 Filter Transactions"
            placement="bottom"
            onClose={() => setShowFilters(false)}
            open={showFilters}
            height="60%"
            styles={{
              body: { padding: '20px' }
            }}
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>💳 Transaction Type</label>
                <Select
                  placeholder="Select type"
                  style={{ width: '100%', height: '48px' }}
                  value={filters.type}
                  onChange={(value) => setFilters(prev => ({ ...prev, type: value }))}
                >
                  <Option value="">All Types</Option>
                  <Option value="credit">💰 Credit</Option>
                  <Option value="debit">💸 Debit</Option>
                </Select>
              </Col>
              <Col span={24}>
                <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>📊 Status</label>
                <Select
                  placeholder="Select status"
                  style={{ width: '100%', height: '48px' }}
                  value={filters.status}
                  onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                >
                  <Option value="">All Status</Option>
                  <Option value="pending">⏳ Pending</Option>
                  <Option value="completed">✅ Completed</Option>
                  <Option value="failed">❌ Failed</Option>
                  <Option value="cancelled">🚫 Cancelled</Option>
                </Select>
              </Col>
              <Col span={24}>
                <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>📅 Date Range</label>
                <RangePicker
                  style={{ width: '100%', height: '48px' }}
                  value={filters.dateRange}
                  onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates }))}
                />
              </Col>
            </Row>
            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <Button
                type="primary"
                block
                style={{
                  height: '48px',
                  borderRadius: '16px',
                  fontSize: '16px',
                  fontWeight: '600',
                  background: 'linear-gradient(135deg, #13c2c2 0%, #36cfc9 100%)',
                  border: 'none'
                }}
                onClick={() => setShowFilters(false)}
              >
                ✅ Apply Filters
              </Button>
            </div>
          </Drawer>
        </>
      ) : (
        /* Desktop Filters */
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Input
                placeholder="Search transactions..."
                prefix={<SearchOutlined />}
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              />
            </Col>
            <Col span={4}>
              <Select
                placeholder="Type"
                style={{ width: '100%' }}
                value={filters.type}
                onChange={(value) => setFilters(prev => ({ ...prev, type: value }))}
              >
                <Option value="">All Types</Option>
                <Option value="credit">Credit</Option>
                <Option value="debit">Debit</Option>
              </Select>
            </Col>
            <Col span={4}>
              <Select
                placeholder="Status"
                style={{ width: '100%' }}
                value={filters.status}
                onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              >
                <Option value="">All Status</Option>
                <Option value="pending">Pending</Option>
                <Option value="completed">Completed</Option>
                <Option value="failed">Failed</Option>
                <Option value="cancelled">Cancelled</Option>
              </Select>
            </Col>
            <Col span={6}>
              <RangePicker
                style={{ width: '100%' }}
                value={filters.dateRange}
                onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates }))}
              />
            </Col>
            <Col span={4}>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchTransactions}
                loading={loading}
              >
                Refresh
              </Button>
            </Col>
          </Row>
        </Card>
      )}

      {/* Transactions List/Table */}
      {viewMode === 'cards' ? (
        /* Mobile Transaction Cards */
        <div>
          {transactions.length === 0 ? (
            <Card style={{ textAlign: 'center', padding: '40px' }}>
              <Empty
                description="No transactions found"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </Card>
          ) : (
            <List
              dataSource={transactions}
              renderItem={(transaction) => (
                <List.Item
                  key={transaction._id}
                  style={{
                    padding: '16px',
                    marginBottom: '12px',
                    background: 'white',
                    borderRadius: '16px',
                    border: '1px solid #f1f5f9',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                  }}
                >
                  <div style={{ width: '100%' }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      marginBottom: 12
                    }}>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                          {transaction.userId?.name || 'Unknown User'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          {transaction.userId?.email || 'No email'}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <Tag 
                          color={getStatusColor(transaction.status)}
                          style={{ 
                            borderRadius: '20px',
                            fontWeight: '600',
                            fontSize: '12px',
                            marginBottom: 4
                          }}
                        >
                          {transaction.status?.toUpperCase()}
                        </Tag>
                        <Tag 
                          color={getTypeColor(transaction.type)}
                          style={{ 
                            borderRadius: '20px',
                            fontWeight: '600',
                            fontSize: '12px'
                          }}
                        >
                          {transaction.type === 'credit' ? '💰 CREDIT' : '💸 DEBIT'}
                        </Tag>
                      </div>
                    </div>
                    
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: 12
                    }}>
                      <span style={{ fontSize: '13px', color: '#64748b' }}>
                        📝 {transaction.description || 'Transaction'}
                      </span>
                      <span style={{ 
                        fontSize: '18px', 
                        fontWeight: '700',
                        color: transaction.type === 'credit' ? '#10b981' : '#ef4444'
                      }}>
                        {transaction.type === 'credit' ? '+' : '-'}₹{Math.abs(transaction.amount)?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                    
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      paddingTop: 12,
                      borderTop: '1px solid #f1f5f9'
                    }}>
                      <span style={{ fontSize: '12px', color: '#64748b' }}>
                        📅 {new Date(transaction.createdAt).toLocaleDateString()}
                      </span>
                      <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => setDetailsModal({ visible: true, transaction })}
                        style={{ padding: 0, height: 'auto' }}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                </List.Item>
              )}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                onChange: (page, pageSize) => {
                  setPagination(prev => ({ ...prev, current: page, pageSize }));
                },
                showSizeChanger: false,
                showQuickJumper: false,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} of ${total} transactions`,
                style: { textAlign: 'center', marginTop: 16 }
              }}
            />
          )}
        </div>
      ) : (
        /* Desktop Transaction Table */
        <Card>
          <Table
            dataSource={transactions}
            rowKey="_id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              onChange: (page, pageSize) => {
                setPagination(prev => ({ ...prev, current: page, pageSize }));
              },
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} of ${total} transactions`
            }}
            scroll={{ x: 1200 }}
            columns={[
              {
                title: 'User',
                dataIndex: ['userId', 'name'],
                key: 'user',
                render: (name, record) => (
                  <div>
                    <div style={{ fontWeight: '600' }}>{name || 'Unknown'}</div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                      {record.userId?.email || 'No email'}
                    </div>
                  </div>
                ),
                width: 200
              },
              {
                title: 'Type',
                dataIndex: 'type',
                key: 'type',
                render: (type) => (
                  <Tag color={getTypeColor(type)}>
                    {type?.toUpperCase()}
                  </Tag>
                ),
                width: 100
              },
              {
                title: 'Amount',
                dataIndex: 'amount',
                key: 'amount',
                render: (amount, record) => (
                  <span style={{
                    fontWeight: 'bold',
                    color: record.type === 'credit' ? '#52c41a' : '#ff4d4f'
                  }}>
                    {record.type === 'credit' ? '+' : '-'}₹{Math.abs(amount)?.toFixed(2)}
                  </span>
                ),
                width: 120,
                align: 'right'
              },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: (status) => (
                  <Tag color={getStatusColor(status)}>
                    {status?.toUpperCase()}
                  </Tag>
                ),
                width: 100
              },
              {
                title: 'Description',
                dataIndex: 'description',
                key: 'description',
                ellipsis: true,
                width: 200
              },
              {
                title: 'Date',
                dataIndex: 'createdAt',
                key: 'createdAt',
                render: (date) => new Date(date).toLocaleDateString(),
                width: 120
              },
              {
                title: 'Actions',
                key: 'actions',
                render: (_, record) => (
                  <Space>
                    <Button
                      type="link"
                      icon={<EyeOutlined />}
                      onClick={() => setDetailsModal({ visible: true, transaction: record })}
                    >
                      View
                    </Button>
                  </Space>
                ),
                width: 100,
                fixed: 'right'
              }
            ]}
          />
        </Card>
      )}

      {/* Transaction Details Modal */}
      <Modal
        title="💳 Transaction Details"
        open={detailsModal.visible}
        onCancel={() => setDetailsModal({ visible: false, transaction: null })}
        footer={null}
        width={isMobile ? '100%' : 600}
        style={{ top: isMobile ? 0 : 20 }}
      >
        {detailsModal.transaction && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="User">
              {detailsModal.transaction.userId?.name || 'Unknown'}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {detailsModal.transaction.userId?.email || 'No email'}
            </Descriptions.Item>
            <Descriptions.Item label="Type">
              <Tag color={getTypeColor(detailsModal.transaction.type)}>
                {detailsModal.transaction.type?.toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Amount">
              <span style={{
                fontWeight: 'bold',
                color: detailsModal.transaction.type === 'credit' ? '#52c41a' : '#ff4d4f'
              }}>
                {detailsModal.transaction.type === 'credit' ? '+' : '-'}₹{Math.abs(detailsModal.transaction.amount)?.toFixed(2)}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getStatusColor(detailsModal.transaction.status)}>
                {detailsModal.transaction.status?.toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Description">
              {detailsModal.transaction.description || 'No description'}
            </Descriptions.Item>
            <Descriptions.Item label="Date">
              {new Date(detailsModal.transaction.createdAt).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Transaction ID">
              <Text code>{detailsModal.transaction._id}</Text>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default Transactions;
